'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';

const steps = [
  {
    step: 1, title: 'Pilih Kavling', icon: 'MagnifyingGlassIcon',
    desc: 'Jelajahi semua kavling tersedia melalui marketplace atau siteplan interaktif. Filter berdasarkan lokasi, harga, luas, dan status.',
    tips: ['Gunakan filter untuk mempersempit pilihan', 'Cek siteplan untuk melihat posisi kavling', 'Bandingkan beberapa kavling sebelum memutuskan'],
    faq: [{ q: 'Apakah bisa memilih nomor kavling sendiri?', a: 'Ya, Anda dapat memilih nomor kavling yang masih tersedia melalui siteplan interaktif.' }],
  },
  {
    step: 2, title: 'Konsultasi', icon: 'ChatBubbleLeftRightIcon',
    desc: 'Hubungi tim BETALAND.ID via WhatsApp untuk mendapatkan informasi lengkap tentang kavling pilihan Anda. Konsultasi gratis, tanpa syarat.',
    tips: ['Siapkan pertanyaan sebelum menghubungi', 'Tanyakan tentang skema pembayaran', 'Minta informasi legalitas lengkap'],
    faq: [{ q: 'Berapa lama waktu respons tim?', a: 'Tim kami merespons dalam waktu kurang dari 1 jam pada jam kerja (08.00-17.00 WIB).' }],
  },
  {
    step: 3, title: 'Survey Lokasi', icon: 'MapPinIcon',
    desc: 'Kunjungi lokasi kavling bersama tim profesional kami. Survey gratis, kami akan menemani Anda melihat langsung kondisi kavling.',
    tips: ['Jadwalkan survey di waktu yang nyaman', 'Bawa keluarga untuk pertimbangan bersama', 'Perhatikan akses jalan dan lingkungan sekitar'],
    faq: [{ q: 'Apakah survey dikenakan biaya?', a: 'Survey lokasi sepenuhnya gratis. Tim kami siap menemani Anda kapan saja.' }],
  },
  {
    step: 4, title: 'Booking', icon: 'DocumentCheckIcon',
    desc: 'Amankan kavling pilihan Anda dengan membayar booking fee. Kavling akan di-hold selama proses administrasi berlangsung.',
    tips: ['Booking fee mulai Rp 5.000.000', 'Booking fee diperhitungkan sebagai DP', 'Kavling di-hold 7 hari setelah booking'],
    faq: [{ q: 'Apakah booking fee bisa dikembalikan?', a: 'Booking fee dapat dikembalikan jika pembatalan dilakukan dalam 3x24 jam setelah booking.' }],
  },
  {
    step: 5, title: 'Pembayaran', icon: 'CreditCardIcon',
    desc: 'Pilih skema pembayaran yang sesuai: tunai keras, cicilan langsung developer, atau KPR bank. Semua skema tersedia dengan syarat yang jelas.',
    tips: ['Tunai keras mendapat diskon 5%', 'Cicilan tersedia 12-36 bulan', 'KPR tersedia untuk kavling tertentu'],
    faq: [{ q: 'Apakah ada cicilan tanpa bunga?', a: 'Tersedia program cicilan 0% untuk periode tertentu. Hubungi tim kami untuk informasi promo terkini.' }],
  },
  {
    step: 6, title: 'Proses Legalitas', icon: 'ShieldCheckIcon',
    desc: 'Tim legal BETALAND.ID akan memproses balik nama sertifikat dan serah terima dokumen SHM atas nama Anda.',
    tips: ['Proses balik nama 1-3 bulan', 'Didampingi notaris/PPAT terpercaya', 'Dokumen lengkap diserahkan setelah lunas'],
    faq: [{ q: 'Berapa biaya balik nama?', a: 'Biaya balik nama (BPHTB + PPh) ditanggung pembeli sesuai ketentuan pemerintah. Beberapa promo menawarkan subsidi BPHTB.' }],
  },
];

export default function CaraMembeliPage() {
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-10">
        <div className="text-center mb-12">
          <nav className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
            <Link href="/homepage" className="hover:text-primary">Beranda</Link>
            <Icon name="ChevronRightIcon" size={14} />
            <span className="text-foreground font-medium">Cara Membeli</span>
          </nav>
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Panduan Pembelian</p>
          <h1 className="text-4xl font-bold text-foreground mb-4">6 Langkah Mudah Miliki Kavling</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">Proses pembelian kavling BETALAND.ID dirancang sesederhana mungkin. Kami dampingi setiap langkahnya.</p>
        </div>

        {/* Steps */}
        <div className="space-y-5 mb-16">
          {steps.map((s) => (
            <div key={s.step} className="bg-card rounded-2xl border border-border overflow-hidden shadow-card">
              <button
                onClick={() => setActiveStep(activeStep === s.step ? null : s.step)}
                className="w-full flex items-center gap-5 p-6 text-left hover:bg-muted/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {s.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <Icon name={s.icon as any} size={18} className="text-primary" />
                    <h3 className="font-bold text-foreground text-lg">{s.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
                <Icon name={activeStep === s.step ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} className="text-muted-foreground flex-shrink-0" />
              </button>
              {activeStep === s.step && (
                <div className="px-6 pb-6 border-t border-border pt-5 grid sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-foreground text-sm mb-3">Tips:</h4>
                    <ul className="space-y-2">
                      {s.tips.map((tip) => (
                        <li key={tip} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Icon name="CheckCircleIcon" size={14} className="text-primary mt-0.5 flex-shrink-0" /> {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-sm mb-3">FAQ:</h4>
                    {s.faq.map((f) => (
                      <div key={f.q} className="bg-muted rounded-xl p-4">
                        <p className="font-medium text-foreground text-sm mb-1">{f.q}</p>
                        <p className="text-sm text-muted-foreground">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Siap Mulai Proses Pembelian?</h2>
          <p className="text-white/70 mb-6">Hubungi tim kami atau langsung pilih kavling yang Anda inginkan.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/6281282760110?text=Halo%20BETALAND.ID%2C%20saya%20ingin%20memulai%20proses%20pembelian%20kavling." target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#1ebe5d] transition-colors">
              <Icon name="ChatBubbleLeftRightIcon" size={18} /> Chat WhatsApp
            </a>
            <Link href="/kavling" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-bold rounded-xl hover:bg-white/90 transition-colors">
              <Icon name="Squares2X2Icon" size={18} /> Lihat Kavling
            </Link>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
